import { useState, useEffect } from 'react';
import { X, Users, MessageCircle, UserMinus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArcImage as Image } from '@/components/ui/arc-image';
import { Link } from 'react-router-dom';
import { getOrCreateDirectConversationAction } from '@/lib/messages/mutations';
import { removeFriendAction } from '@/lib/friends/mutations';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import type { Friend } from '@/lib/db/queries/friends';
import type { Profile } from '@/types';
import { apiFetch } from '@/lib/api-fetch';

interface FriendsDrawerClientProps {
  profile: Profile | null;
  isOpen: boolean;
  onClose: () => void;
}

export function FriendsDrawerClient({ profile, isOpen, onClose }: FriendsDrawerClientProps) {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'online' | 'offline'>('all');
  const navigate = useNavigate();

  // Fetch friends when drawer opens
  useEffect(() => {
    console.log('[FriendsDrawerClient] Drawer opened, profile:', profile, 'isOpen:', isOpen);
    if (isOpen) {
      if (profile?.id) {
        loadFriends();
      } else {
        setLoading(false);
        setFriends([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profile?.id]);

  const loadFriends = async () => {
    if (!profile?.id) return;
    
    setLoading(true);
    try {
      const response = await apiFetch(`/api/friends?userId=${profile.id}`);
      if (response.ok) {
        const data = await response.json();
        setFriends(data.friends || []);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
      toast.error('Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async (friendId: string) => {
    try {
      const { conversationId } = await getOrCreateDirectConversationAction(friendId);
      navigate(`/messages/${conversationId}`);
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to start conversation');
    }
  };

  const handleRemoveFriend = async (friendId: string) => {
    if (!confirm('Are you sure you want to remove this friend?')) {
      return;
    }

    try {
      await removeFriendAction(friendId);
      toast.success('Friend removed');
      // Remove from local state
      setFriends(friends.filter(f => f.friendId !== friendId));
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove friend');
    }
  };

  const onlineFriends = friends.filter(f => f.friend?.onlineStatus?.isOnline);
  const offlineFriends = friends.filter(f => !f.friend?.onlineStatus?.isOnline);

  const displayedFriends = activeTab === 'online' 
    ? onlineFriends 
    : activeTab === 'offline' 
    ? offlineFriends 
    : friends;

  if (!isOpen) return null;

  if (!profile) {
    return (
      <div className="fixed top-16 right-4 w-96 bg-arc-panel border border-arc-border rounded-lg shadow-2xl z-[55] flex flex-col max-h-[600px] animate-in fade-in slide-in-from-top-2 duration-200">
        <div className="flex items-center justify-between p-4 border-b border-arc-border">
          <h2 className="text-lg font-semibold text-arc-text-main">Social</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-8 text-center text-arc-text-muted text-sm">
          Please sign in to view friends
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-16 right-4 w-96 bg-arc-panel border border-arc-border rounded-lg shadow-2xl z-[55] flex flex-col max-h-[600px] animate-in fade-in slide-in-from-top-2 duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-arc-border bg-arc-panel-soft">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-arc-primary" />
          <h2 className="text-lg font-semibold text-arc-text-main">Friends</h2>
          <span className="text-sm text-arc-text-muted">({friends.length})</span>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 hover:bg-arc-panel">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="mx-4 mt-3 mb-2 grid grid-cols-3">
          <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
          <TabsTrigger value="online" className="text-xs">
            Online ({onlineFriends.length})
          </TabsTrigger>
          <TabsTrigger value="offline" className="text-xs">
            Offline ({offlineFriends.length})
          </TabsTrigger>
        </TabsList>

        {/* Friends List */}
        <ScrollArea className="flex-1 px-4 pb-4">
          {displayedFriends.length === 0 ? (
            <div className="text-center py-8 text-arc-text-muted text-sm">
              {activeTab === 'online' 
                ? 'No friends online' 
                : activeTab === 'offline'
                ? 'No friends offline'
                : 'No friends yet'}
            </div>
          ) : (
            <div className="space-y-2">
              {displayedFriends.map((friend) => {
                const friendProfile = friend.friend;
                if (!friendProfile) return null;

                const isOnline = friendProfile.onlineStatus?.isOnline || false;
                const currentGame = friendProfile.onlineStatus?.currentGame;

                return (
                  <div
                    key={friend.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-arc-panel-soft transition-colors group"
                  >
                    <Link to={`/profile/${friendProfile.id}`} className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-arc-panel-soft border border-arc-border overflow-hidden">
                        {friendProfile.avatarUrl ? (
                          <Image
                            src={friendProfile.avatarUrl}
                            alt={friendProfile.displayName}
                            width={48}
                            height={48}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-sm text-arc-text-muted">
                            {friendProfile.displayName[0]}
                          </div>
                        )}
                      </div>
                      {/* Online status indicator */}
                      <div
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-arc-panel ${
                          isOnline ? 'bg-green-500' : 'bg-arc-text-muted'
                        }`}
                      />
                    </Link>

                    <div className="flex-1 min-w-0">
                      <Link to={`/profile/${friendProfile.id}`}>
                        <div className="font-semibold text-arc-text-main hover:text-arc-primary transition-colors truncate">
                          {friendProfile.displayName}
                        </div>
                      </Link>
                      {currentGame ? (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="w-4 h-4 rounded bg-arc-panel-soft border border-arc-border overflow-hidden flex-shrink-0">
                            <Image
                              src={currentGame.thumbnailUrl}
                              alt={currentGame.title}
                              width={16}
                              height={16}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-xs text-arc-text-muted truncate">
                            Playing {currentGame.title}
                          </span>
                        </div>
                      ) : (
                        <div className="text-xs text-arc-text-muted">
                          {isOnline ? 'Online' : 'Offline'}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleStartChat(friendProfile.id)}
                        className="h-8 w-8"
                        title="Start chat"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveFriend(friendProfile.id)}
                        className="h-8 w-8 text-arc-danger hover:text-arc-danger"
                        title="Remove friend"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </Tabs>
    </div>
  );
}

